import {
  isCourtesyVehicleUnavailableError,
  type CourtesyVehicle,
  type ReserveCourtesyLoanData,
} from '@/hooks/useCourtesyVehicles';

/** The courtesy car picked in the create wizard (#4103). Kept in the draft. */
export interface CreateCourtesySelection {
  id: string;
  /** Plate (and name) for the summary and toasts — the list may be gone by then. */
  label: string;
}

export function courtesyVehicleLabel(
  car: Pick<CourtesyVehicle, 'registrationNumber' | 'displayName'>,
): string {
  return car.displayName ? `${car.registrationNumber} · ${car.displayName}` : car.registrationNumber;
}

export type CourtesyAfterCreateOutcome = 'none' | 'reserved' | 'unavailable' | 'failed';

/**
 * Reserve the courtesy car picked in the create wizard for the work order
 * that was just saved (#4103). Never throws: the work order already exists,
 * so a refused reservation is reported to the caller, not rolled back.
 */
export async function reserveCourtesyAfterCreate(args: {
  courtesyVehicleId: string | null;
  workOrderId: string;
  range: { plannedStart: string; plannedEnd: string } | null;
  reserve: (data: ReserveCourtesyLoanData) => Promise<unknown>;
}): Promise<CourtesyAfterCreateOutcome> {
  const { courtesyVehicleId, workOrderId, range, reserve } = args;
  if (!courtesyVehicleId || !range) return 'none';
  try {
    await reserve({
      courtesyVehicleId,
      plannedStart: range.plannedStart,
      plannedEnd: range.plannedEnd,
      bookingId: workOrderId,
    });
    return 'reserved';
  } catch (error) {
    return isCourtesyVehicleUnavailableError(error) ? 'unavailable' : 'failed';
  }
}
